class StepTracker {
    constructor() {
        this.steps = [];
        this.isTracking = false;
        this.startedAt = null;
        this.lastUrl = "";
        this.STORAGE_KEY = "uxbert-navigation-steps";
        this.SESSION_ID_KEY = "uxbert-session-id";
        this.MAX_STEPS = 100;
        // For throttling scroll events
        this.scrollTimeout = null;
        this.lastScrollTime = 0;
        this.SCROLL_THROTTLE = 1000; // 1 second
        // For tracking tab visibility
        this.tabHiddenAt = null;
        // For debouncing input events
        this.inputTimeout = null;
        this.INPUT_DEBOUNCE = 500; // 500ms
        // For debouncing text selection events
        this.selectionTimeout = null;
        this.SELECTION_DEBOUNCE = 1000; // 1 second
        /**
         * Handle URL change
         */
        this.handleUrlChange = () => {
            if (!this.isTracking) {
                return;
            }
            this.recordStep("navigation");
        };
        /**
         * Handle page load
         */
        this.handlePageLoad = () => {
            if (!this.isTracking) {
                return;
            }
            this.recordStep("load");
        };
        /**
         * Handle click event
         */
        this.handleClick = (event) => {
            this.recordClick(event);
        };
        /**
         * Handle input event
         */
        this.handleInput = (event) => {
            this.recordInput(event);
        };
        /**
         * Handle form submit event
         */
        this.handleSubmit = (event) => {
            this.recordSubmit(event);
        };
        /**
         * Handle scroll event
         */
        this.handleScroll = () => {
            this.recordScroll();
        };
        /**
         * Handle JavaScript errors
         */
        this.handleError = (event) => {
            this.recordError(event.message, event.filename, event.lineno, event.colno);
        };
        /**
         * Handle unhandled promise rejections
         */
        this.handleUnhandledRejection = (event) => {
            const message = event.reason?.message || String(event.reason) || "Unhandled Promise Rejection";
            this.recordError(message);
        };
        /**
         * Handle visibility change (tab hidden/visible)
         */
        this.handleVisibilityChange = () => {
            this.recordVisibilityChange();
        };
        /**
         * Handle text selection event
         */
        this.handleSelectionChange = () => {
            this.recordTextSelection();
        };
        // Generate a unique session ID for this page load
        this.currentSessionId = this.generateSessionId();
        // Check browser environment and initialize
        if (typeof window !== "undefined" && typeof history !== "undefined") {
            // Store original methods
            this.originalPushState = history.pushState.bind(history);
            this.originalReplaceState = history.replaceState.bind(history);
        }
        else {
            // Provide dummy implementations for SSR
            this.originalPushState = (() => { });
            this.originalReplaceState = (() => { });
        }
        // ALWAYS clear storage on page load/refresh
        // This ensures we start with a clean slate on every page load
        this.clearStorage();
        console.log("[Aminah Step Tracker] Page loaded - starting fresh session");
        // Store current session ID in sessionStorage (cleared on page close/refresh)
        this.storeSessionId();
        // Auto-start tracking if not already tracking
        if (typeof window !== "undefined" && !this.isTracking) {
            this.start();
        }
    }
    /**
     * Start tracking navigation steps
     */
    start() {
        if (typeof window === "undefined") {
            return;
        }
        if (this.isTracking) {
            console.warn("Step tracking is already active");
            return;
        }
        this.isTracking = true;
        this.startedAt = new Date().toISOString();
        this.lastUrl = window.location.href;
        // Record initial step
        this.recordStep("start");
        // Setup listeners for URL changes
        this.setupListeners();
        // Save to storage
        this.saveToStorage();
        console.log("Step tracking started");
    }
    /**
     * Stop tracking navigation steps
     */
    stop() {
        if (!this.isTracking) {
            return;
        }
        this.isTracking = false;
        // Remove listeners
        this.removeListeners();
        // Save final state to storage
        this.saveToStorage();
        console.log("Step tracking stopped");
    }
    /**
     * Clear all recorded steps
     */
    clear() {
        this.steps = [];
        this.startedAt = null;
        this.lastUrl = "";
        if (typeof window !== "undefined") {
            if (typeof localStorage !== "undefined") {
                localStorage.removeItem(this.STORAGE_KEY);
            }
            if (typeof sessionStorage !== "undefined") {
                sessionStorage.removeItem(this.SESSION_ID_KEY);
            }
        }
        console.log("Step tracking cleared");
    }
    /**
     * Reset and restart tracking
     */
    restart() {
        this.stop();
        this.clear();
        this.start();
    }
    /**
     * Get all recorded steps
     */
    getSteps() {
        return [...this.steps];
    }
    /**
     * Get tracking status
     */
    getStatus() {
        return {
            isTracking: this.isTracking,
            stepCount: this.steps.length,
            startedAt: this.startedAt,
        };
    }
    /**
     * Record a navigation step
     */
    recordStep(action = "navigation") {
        if (typeof window === "undefined") {
            return;
        }
        const currentUrl = window.location.href;
        // Don't record if URL hasn't changed (unless it's the start action)
        if (action !== "start" && currentUrl === this.lastUrl) {
            return;
        }
        const step = {
            url: currentUrl,
            title: document.title || "",
            timestamp: new Date().toISOString(),
            referrer: this.lastUrl || document.referrer || "",
            action,
        };
        this.addStep(step);
        this.lastUrl = currentUrl;
    }
    /**
     * Add a step to the tracking array
     */
    addStep(step) {
        this.steps.push(step);
        // Log step to console
        console.log("[Aminah Step Tracker]", {
            action: step.action,
            timestamp: step.timestamp,
            url: step.url,
            details: step,
        });
        // Limit stored steps to prevent memory issues
        if (this.steps.length > this.MAX_STEPS) {
            this.steps = this.steps.slice(-this.MAX_STEPS);
        }
        // Save to storage
        this.saveToStorage();
    }
    /**
     * Generate a CSS selector for an element
     */
    getElementSelector(element) {
        if (element.id) {
            return `#${element.id}`;
        }
        const path = [];
        let current = element;
        while (current && current.nodeType === Node.ELEMENT_NODE) {
            let selector = current.tagName.toLowerCase();
            if (current.className && typeof current.className === "string") {
                const classes = current.className.trim().split(/\s+/).slice(0, 2); // Limit to 2 classes
                if (classes.length > 0 && classes[0]) {
                    selector += "." + classes.join(".");
                }
            }
            path.unshift(selector);
            // Stop at first element with ID or after 4 levels
            if (current.id || path.length >= 4) {
                break;
            }
            current = current.parentElement;
        }
        return path.join(" > ");
    }
    /**
     * Get visible text content from element (limited length)
     */
    getElementText(element) {
        const text = element.textContent?.trim() || "";
        return text.length > 0 ? text.substring(0, 100) : undefined;
    }
    /**
     * Record a click event
     */
    recordClick(event) {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const target = event.target;
        if (!target || !target.tagName) {
            return;
        }
        // Skip clicks on Aminah's own UI elements
        if (target.closest("[data-aminah]") || target.closest(".aminah-")) {
            return;
        }
        const step = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            title: document.title || "",
            action: "click",
            elementTag: target.tagName.toLowerCase(),
            elementText: this.getElementText(target),
            elementId: target.id || undefined,
            elementClass: target.className ? String(target.className).substring(0, 100) : undefined,
            selector: this.getElementSelector(target),
            x: event.clientX,
            y: event.clientY,
        };
        this.addStep(step);
    }
    /**
     * Record an input event (debounced)
     */
    recordInput(event) {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const target = event.target;
        if (!target || !target.tagName) {
            return;
        }
        // Skip Aminah's own inputs
        if (target.closest("[data-aminah]")) {
            return;
        }
        // Debounce input events
        if (this.inputTimeout) {
            clearTimeout(this.inputTimeout);
        }
        this.inputTimeout = setTimeout(() => {
            const step = {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                title: document.title || "",
                action: "input",
                elementTag: target.tagName.toLowerCase(),
                elementId: target.id || undefined,
                elementName: target.name || undefined,
                elementType: target.type || undefined,
                selector: this.getElementSelector(target),
                valueLength: target.value?.length || 0,
            };
            this.addStep(step);
        }, this.INPUT_DEBOUNCE);
    }
    /**
     * Record form submit
     */
    recordSubmit(event) {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const target = event.target;
        if (!target || target.tagName !== "FORM") {
            return;
        }
        // Skip Aminah's own forms
        if (target.closest("[data-aminah]")) {
            return;
        }
        const formElements = target.elements;
        let fieldCount = 0;
        for (let i = 0; i < formElements.length; i++) {
            const element = formElements[i];
            if (element.tagName === "INPUT" || element.tagName === "TEXTAREA" || element.tagName === "SELECT") {
                fieldCount++;
            }
        }
        const step = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            title: document.title || "",
            action: "submit",
            formId: target.id || undefined,
            formName: target.name || undefined,
            selector: this.getElementSelector(target),
            fieldCount,
        };
        this.addStep(step);
    }
    /**
     * Record scroll event (throttled)
     */
    recordScroll() {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const now = Date.now();
        // Throttle scroll events
        if (now - this.lastScrollTime < this.SCROLL_THROTTLE) {
            if (this.scrollTimeout) {
                clearTimeout(this.scrollTimeout);
            }
            this.scrollTimeout = setTimeout(() => {
                this.recordScroll();
            }, this.SCROLL_THROTTLE);
            return;
        }
        this.lastScrollTime = now;
        const scrollY = window.scrollY || window.pageYOffset;
        const scrollX = window.scrollX || window.pageXOffset;
        const maxScroll = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
        const scrollPercentage = Math.round((scrollY / maxScroll) * 100);
        const step = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            title: document.title || "",
            action: "scroll",
            scrollX,
            scrollY,
            scrollPercentage: Math.min(scrollPercentage, 100),
        };
        this.addStep(step);
    }
    /**
     * Record JavaScript error
     */
    recordError(message, source, line, column) {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const step = {
            timestamp: new Date().toISOString(),
            url: window.location.href,
            title: document.title || "",
            action: "error",
            errorType: "javascript",
            message: message.substring(0, 500), // Limit message length
            source,
            line,
            column,
        };
        this.addStep(step);
    }
    /**
     * Record text selection event (debounced)
     */
    recordTextSelection() {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        // Debounce selection events
        if (this.selectionTimeout) {
            clearTimeout(this.selectionTimeout);
        }
        this.selectionTimeout = setTimeout(() => {
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || !selection.toString().trim()) {
                return;
            }
            const selectedText = selection.toString().trim();
            if (selectedText.length < 3) {
                // Ignore very short selections
                return;
            }
            const range = selection.getRangeAt(0);
            const container = range.commonAncestorContainer;
            const element = container.nodeType === Node.ELEMENT_NODE
                ? container
                : container.parentElement;
            if (!element) {
                return;
            }
            // Skip selections in Aminah's own UI elements
            if (element.closest("[data-aminah]") || element.closest(".aminah-") || element.closest("[data-uxbert-aminah]")) {
                return;
            }
            const step = {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                title: document.title || "",
                action: "text_selection",
                selectedText: selectedText.substring(0, 200), // Limit to 200 chars
                elementTag: element.tagName.toLowerCase(),
                elementId: element.id || undefined,
                elementClass: element.className ? String(element.className).substring(0, 100) : undefined,
                selector: this.getElementSelector(element),
            };
            this.addStep(step);
        }, this.SELECTION_DEBOUNCE);
    }
    /**
     * Record visibility change (tab hidden/visible)
     */
    recordVisibilityChange() {
        if (!this.isTracking || typeof window === "undefined") {
            return;
        }
        const isHidden = document.hidden;
        const now = Date.now();
        if (isHidden) {
            this.tabHiddenAt = now;
            const step = {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                title: document.title || "",
                action: "tab_hidden",
            };
            this.addStep(step);
        }
        else if (this.tabHiddenAt) {
            const hiddenDuration = now - this.tabHiddenAt;
            const step = {
                timestamp: new Date().toISOString(),
                url: window.location.href,
                title: document.title || "",
                action: "tab_visible",
                hiddenDuration,
            };
            this.addStep(step);
            this.tabHiddenAt = null;
        }
    }
    /**
     * Setup event listeners for URL changes and user interactions
     */
    setupListeners() {
        if (typeof window === "undefined") {
            return;
        }
        // Navigation events
        window.addEventListener("popstate", this.handleUrlChange);
        window.addEventListener("hashchange", this.handleUrlChange);
        window.addEventListener("load", this.handlePageLoad);
        // Override history methods to detect pushState and replaceState
        history.pushState = (...args) => {
            this.originalPushState(...args);
            this.handleUrlChange();
        };
        history.replaceState = (...args) => {
            this.originalReplaceState(...args);
            this.handleUrlChange();
        };
        // User interaction events
        document.addEventListener("click", this.handleClick, true);
        document.addEventListener("input", this.handleInput, true);
        document.addEventListener("submit", this.handleSubmit, true);
        window.addEventListener("scroll", this.handleScroll, { passive: true });
        document.addEventListener("selectionchange", this.handleSelectionChange);
        // Error tracking
        window.addEventListener("error", this.handleError);
        window.addEventListener("unhandledrejection", this.handleUnhandledRejection);
        // Visibility tracking
        document.addEventListener("visibilitychange", this.handleVisibilityChange);
    }
    /**
     * Remove event listeners
     */
    removeListeners() {
        if (typeof window === "undefined") {
            return;
        }
        // Navigation events
        window.removeEventListener("popstate", this.handleUrlChange);
        window.removeEventListener("hashchange", this.handleUrlChange);
        window.removeEventListener("load", this.handlePageLoad);
        // Restore original history methods
        history.pushState = this.originalPushState;
        history.replaceState = this.originalReplaceState;
        // User interaction events
        document.removeEventListener("click", this.handleClick, true);
        document.removeEventListener("input", this.handleInput, true);
        document.removeEventListener("submit", this.handleSubmit, true);
        window.removeEventListener("scroll", this.handleScroll);
        document.removeEventListener("selectionchange", this.handleSelectionChange);
        // Error tracking
        window.removeEventListener("error", this.handleError);
        window.removeEventListener("unhandledrejection", this.handleUnhandledRejection);
        // Visibility tracking
        document.removeEventListener("visibilitychange", this.handleVisibilityChange);
        // Clear timeouts
        if (this.scrollTimeout) {
            clearTimeout(this.scrollTimeout);
            this.scrollTimeout = null;
        }
        if (this.inputTimeout) {
            clearTimeout(this.inputTimeout);
            this.inputTimeout = null;
        }
        if (this.selectionTimeout) {
            clearTimeout(this.selectionTimeout);
            this.selectionTimeout = null;
        }
    }
    /**
     * Generate a unique session ID for this page load
     */
    generateSessionId() {
        return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    }
    /**
     * Store current session ID in sessionStorage
     */
    storeSessionId() {
        if (typeof window === "undefined" || typeof sessionStorage === "undefined") {
            return;
        }
        try {
            sessionStorage.setItem(this.SESSION_ID_KEY, this.currentSessionId);
        }
        catch (e) {
            console.warn("Failed to store session ID:", e);
        }
    }
    /**
     * Clear storage (both localStorage and sessionStorage)
     */
    clearStorage() {
        if (typeof window === "undefined") {
            return;
        }
        try {
            if (typeof localStorage !== "undefined") {
                localStorage.removeItem(this.STORAGE_KEY);
            }
            if (typeof sessionStorage !== "undefined") {
                // Clear the old session ID so we can set a fresh one
                sessionStorage.removeItem(this.SESSION_ID_KEY);
            }
        }
        catch (e) {
            console.warn("Failed to clear storage:", e);
        }
    }
    /**
     * Save steps to localStorage
     */
    saveToStorage() {
        if (typeof window === "undefined" || typeof localStorage === "undefined") {
            return;
        }
        try {
            const data = {
                steps: this.steps,
                isTracking: this.isTracking,
                startedAt: this.startedAt,
                sessionId: this.currentSessionId,
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        }
        catch (e) {
            console.warn("Failed to save steps to localStorage:", e);
        }
    }
    /**
     * Export steps as JSON string
     */
    exportToJSON() {
        return JSON.stringify({
            steps: this.steps,
            startedAt: this.startedAt,
            totalSteps: this.steps.length,
            exportedAt: new Date().toISOString(),
        }, null, 2);
    }
    /**
     * Get a summary of navigation path
     */
    getSummary() {
        if (this.steps.length === 0) {
            return "No navigation steps recorded";
        }
        return this.steps
            .map((step, index) => {
            const url = new URL(step.url);
            return `${index + 1}. ${step.title || url.pathname} (${step.action})`;
        })
            .join("\n");
    }
    /**
     * Clean up when destroying the tracker
     */
    destroy() {
        this.stop();
        this.removeListeners();
    }
}
export default StepTracker;
//# sourceMappingURL=step-tracker.js.map